/**
 * The standalone REGISTERS — Figma page 04's two cross-CRF reads (v7 Block B) and
 * page 05's three (Block C), mounted through the `routes.extra.ts` seam.
 *
 * =============================================================================
 * WHY THESE ARE SIDEBAR DESTINATIONS AND THE CONFIRMATION REGISTER IS NOT
 * =============================================================================
 * `domain/nav.ts` has reserved `testing` and `dl-files` as sidebar rows since
 * Phase 3, both `available: false` — mounting them is what turns two dead
 * reservations into screens. That is the opposite of the CRF confirmation
 * register, which rides under `crfs/` with no nav row because the design's own
 * breadcrumb calls it a CRF sub-page. The registry is the authority on which is
 * which; neither decision is re-derived here.
 *
 * NO PROVIDER LAYOUT, DELIBERATELY. The CRF routes wrap themselves in one so a
 * toast survives create → record navigation. Nothing on either register mutates
 * anything: they are two read-only tables whose only action is downloading a file
 * the operator already asked for. A provider that exists to carry a toast nothing
 * raises is a wrapper with no reader.
 *
 * Both paths sit INSIDE `RequireCountry` + `AppShell` like every other country
 * route, so the pages can rely on `useCountryCode()` and on a resolved session.
 */

import type { RouteObject } from 'react-router-dom';

import { TestingRegisterPage } from './TestingRegisterPage';
import { DlFilesRegisterPage } from './DlFilesRegisterPage';
import { DeploymentRegisterPage } from './DeploymentRegisterPage';
import { DeployedProjectsPage } from './DeployedProjectsPage';
import { SignoffQueuePage } from './SignoffQueuePage';
import { AcceptanceChasePage } from './AcceptanceChasePage';

/*
 * THE PATHS ARE THE REGISTRY'S, NOT MINE. Each of these five strings is copied
 * from the `path` its `domain/nav.ts` row has carried since Phase 3 — including
 * `deployed`, which is deliberately NOT `deployed-projects` even though the API
 * route is. The sidebar link and the router have to agree, and the registry is
 * the side that already had an opinion; inventing a tidier URL here would give
 * the nav a dead link and no error to explain it.
 */
export const registerRoutes: RouteObject[] = [
  { path: 'testing', element: <TestingRegisterPage /> },
  { path: 'dl-files', element: <DlFilesRegisterPage /> },
  { path: 'deployment', element: <DeploymentRegisterPage /> },
  { path: 'deployed', element: <DeployedProjectsPage /> },
  { path: 'signoffs', element: <SignoffQueuePage /> },
  /*
   * THE ACCEPTANCE CHASE (Train F, fork 21) — a SIBLING of the queue, exactly as
   * the API mounts it (`GET /countries/:cc/signoffs/acceptance-chase` beside
   * `GET /countries/:cc/signoffs`), so the two URLs relate the way the two
   * registers do: disjoint lists under one signoffs family. Its nav row lives in
   * `ACCEPTANCE_NAV_SECTION` — an AUTHORED registry, because `NAV_SECTIONS` is
   * pinned as "the thirteen destinations the FIGMA sidebar lists" and this
   * destination is not one of them — and the path here is copied from that row,
   * per this file's own rule that the registry is the side with the opinion.
   */
  { path: 'signoffs/acceptance-chase', element: <AcceptanceChasePage /> },
];
